import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Lock as LockIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import {
  createDomainList,
  deleteDomainList,
  domainsToText,
  getDomainLists,
  parseDomainsText,
  updateDomainList,
  validateDomain,
} from '../services/domainListService';
import { IMG } from '../theme/assets';
import type { DomainList } from '../types';
import AppDialog from './AppDialog';

interface DomainListManagerProps {
  open: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

const DomainListManager: React.FC<DomainListManagerProps> = ({ open, onClose, onUpdate }) => {
  const { user } = useAuth();
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();

  // テーマカラー変数
  const accordionBg = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : tokens.paper;
  const accentBg = themeMode === 'dark' ? 'rgba(45, 212, 191, 0.08)' : '#f3faf8';
  const lockIconColor = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.45)';

  // mock .dacc 準拠：枠付きカード型 Accordion（既定の影/罫線/フォーカス枠を消す）
  const accordionSx = {
    mb: 1,
    backgroundColor: accordionBg,
    border: `1px solid ${tokens.line}`,
    borderRadius: '13px !important',
    boxShadow: 'none',
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: '0 0 8px 0' },
    overflow: 'hidden',
  } as const;
  const accordionSummarySx = {
    px: 1.75,
    outline: 'none',
    '& .MuiAccordionSummary-content': { my: 1.25 },
    // 展開/フォーカス時に出る MUI 既定の枠・背景・リップルを完全に消す
    '&.Mui-focusVisible': { backgroundColor: 'transparent !important' },
    '&:focus, &:focus-visible': { outline: 'none', boxShadow: 'none' },
    '&.Mui-expanded': { backgroundColor: 'transparent' },
    '& .MuiTouchRipple-root': { display: 'none' },
  } as const;

  const [domainLists, setDomainLists] = useState<DomainList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 新規作成フォーム
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newDomains, setNewDomains] = useState('');
  const [creating, setCreating] = useState(false);

  // 編集フォーム
  const [editingList, setEditingList] = useState<DomainList | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editDomains, setEditDomains] = useState('');
  const [saving, setSaving] = useState(false);

  const loadDomainLists = useCallback(async () => {
    if (!user?.uid) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getDomainLists();
      if (response.success && response.data) {
        setDomainLists(response.data);
      } else {
        setError(response.error || 'ドメインリストの取得に失敗しました');
      }
    } catch (_err) {
      setError('ドメインリストの取得中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  // データ読み込み
  useEffect(() => {
    if (open && user?.uid) {
      loadDomainLists();
    }
  }, [open, user?.uid, loadDomainLists]);

  // 新規作成
  const handleCreate = async () => {
    if (!user?.uid || !newName.trim()) return;

    const domains = parseDomainsText(newDomains);
    if (domains.length === 0) {
      setError('少なくとも1つのドメインを入力してください');
      return;
    }

    // ドメインバリデーション
    const invalidDomains = domains.filter((d) => !validateDomain(d));
    if (invalidDomains.length > 0) {
      setError(`無効なドメイン形式: ${invalidDomains.slice(0, 3).join(', ')}...`);
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await createDomainList({
        name: newName.trim(),
        description: newDescription.trim(),
        domains,
      });

      if (response.success) {
        setNewName('');
        setNewDescription('');
        setNewDomains('');
        setShowCreateForm(false);
        await loadDomainLists();
        onUpdate?.();
      } else {
        setError(response.error || '作成に失敗しました');
      }
    } catch (_err) {
      setError('作成中にエラーが発生しました');
    } finally {
      setCreating(false);
    }
  };

  // 編集開始
  const handleStartEdit = (list: DomainList) => {
    setEditingList(list);
    setEditName(list.name);
    setEditDescription(list.description);
    setEditDomains(domainsToText(list.domains));
  };

  // 編集保存
  const handleSaveEdit = async () => {
    if (!user?.uid || !editingList) return;

    const domains = parseDomainsText(editDomains);
    if (domains.length === 0) {
      setError('少なくとも1つのドメインを入力してください');
      return;
    }

    // ドメインバリデーション
    const invalidDomains = domains.filter((d) => !validateDomain(d));
    if (invalidDomains.length > 0) {
      setError(`無効なドメイン形式: ${invalidDomains.slice(0, 3).join(', ')}...`);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await updateDomainList(editingList.id, {
        name: editName.trim(),
        description: editDescription.trim(),
        domains,
      });

      if (response.success) {
        setEditingList(null);
        await loadDomainLists();
        onUpdate?.();
      } else {
        setError(response.error || '更新に失敗しました');
      }
    } catch (_err) {
      setError('更新中にエラーが発生しました');
    } finally {
      setSaving(false);
    }
  };

  // 削除
  const handleDelete = async (listId: string) => {
    if (!user?.uid) return;

    if (!window.confirm('このドメインリストを削除しますか？')) return;

    try {
      const response = await deleteDomainList(listId);
      if (response.success) {
        await loadDomainLists();
        onUpdate?.();
      } else {
        setError(response.error || '削除に失敗しました');
      }
    } catch (_err) {
      setError('削除中にエラーが発生しました');
    }
  };

  // システムリストとユーザーリストを分離
  const systemLists = domainLists.filter((list) => list.is_system);
  const userLists = domainLists.filter((list) => !list.is_system);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      icon={<Box component="img" src={IMG.help.domain} alt="" />}
      title="ドメインリストの管理"
      subtitle="検証で照合する信頼ドメインを編集します"
      size="md"
      actions={
        <>
          <Button
            onClick={onClose}
            sx={{
              background: 'linear-gradient(120deg, #0d9488, #5b56e0)',
              color: '#fff',
              fontWeight: 800,
              borderRadius: '11px',
              textTransform: 'none',
              px: 2.5,
              boxShadow: '0 8px 20px rgba(40,50,90,0.2)',
              '&:hover': {
                filter: 'brightness(0.96)',
                background: 'linear-gradient(120deg, #0d9488, #5b56e0)',
              },
            }}
          >
            完了
          </Button>
        </>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* システムテンプレート */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 900, color: tokens.muted }}>
              システムテンプレート
            </Typography>
            <Box
              sx={{
                fontSize: 9.5,
                fontWeight: 800,
                color: '#94a3b8',
                bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.08)' : '#eef1f5',
                borderRadius: 999,
                px: 1,
                py: '1px',
              }}
            >
              編集不可
            </Box>
          </Box>

          {systemLists.map((list) => (
            <Accordion key={list.id} disableGutters elevation={0} sx={accordionSx}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LockIcon sx={{ fontSize: 16, color: lockIconColor }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {list.name}
                  </Typography>
                  <Chip label={`${list.domains.length}件`} size="small" />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1 }}
                >
                  {list.description}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {list.domains.map((domain, index) => (
                    <Chip key={index} label={domain} size="small" variant="outlined" />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}

          <Divider sx={{ my: 3 }} />

          {/* ユーザー作成リスト */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                マイリスト
              </Typography>
              <Typography variant="caption" color="text.secondary">
                独自のドメインリストを作成・管理できます
              </Typography>
            </Box>
            {!showCreateForm && !editingList && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateForm(true)}
                sx={{
                  color: tokens.teal,
                  background: themeMode === 'dark' ? 'rgba(45,212,191,0.12)' : '#eafaf6',
                  border: `1.5px solid ${
                    themeMode === 'dark' ? 'rgba(45,212,191,0.4)' : 'rgba(13,148,136,.4)'
                  }`,
                  borderRadius: '9px',
                  fontWeight: 800,
                  fontSize: 12,
                  textTransform: 'none',
                  px: 1.5,
                  '&:hover': {
                    background: themeMode === 'dark' ? 'rgba(45,212,191,0.18)' : '#dbf3ee',
                  },
                }}
              >
                新しいリスト
              </Button>
            )}
          </Box>

          {/* 新規作成フォーム */}
          {showCreateForm && (
            <Card
              elevation={0}
              sx={{
                mb: 2,
                backgroundColor: accentBg,
                border: `1px solid ${
                  themeMode === 'dark' ? 'rgba(45,212,191,0.3)' : 'rgba(13,148,136,.3)'
                }`,
                borderRadius: '13px',
                boxShadow: 'none',
                backgroundImage: 'none',
                backdropFilter: 'none',
                '&:hover': {
                  transform: 'none',
                  boxShadow: 'none',
                  borderColor:
                    themeMode === 'dark' ? 'rgba(45,212,191,0.3)' : 'rgba(13,148,136,.3)',
                },
              }}
            >
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: tokens.ink }}>
                  新しいドメインリストを作成
                </Typography>
                <TextField
                  fullWidth
                  label="リスト名"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="説明"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="ドメイン（1行に1つ、またはカンマ区切り）"
                  value={newDomains}
                  onChange={(e) => setNewDomains(e.target.value)}
                  multiline
                  rows={4}
                  size="small"
                  placeholder="example.com&#10;*.gov.jp&#10;news.example.org"
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={creating || !newName.trim()}
                    startIcon={
                      creating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <AddIcon />
                    }
                    sx={{
                      background: tokens.teal,
                      color: '#fff',
                      fontWeight: 800,
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': { background: tokens.teal, filter: 'brightness(0.95)' },
                      '&.Mui-disabled': { background: tokens.line, color: tokens.faint },
                    }}
                  >
                    {creating ? '作成中...' : '作成'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setShowCreateForm(false)}
                    sx={{
                      color: tokens.muted,
                      borderColor: tokens.line,
                      textTransform: 'none',
                      '&:hover': { borderColor: tokens.teal, color: tokens.teal },
                    }}
                  >
                    キャンセル
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* ユーザーリスト一覧 */}
          {userLists.length === 0 && !showCreateForm ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              まだマイリストがありません
            </Typography>
          ) : (
            userLists.map((list) =>
              editingList?.id === list.id ? (
                // 編集フォーム
                <Card
                  key={list.id}
                  elevation={0}
                  sx={{
                    mb: 2,
                    backgroundColor: accentBg,
                    border: `1px solid ${
                      themeMode === 'dark' ? 'rgba(45,212,191,0.3)' : 'rgba(13,148,136,.3)'
                    }`,
                    borderRadius: '13px',
                    boxShadow: 'none',
                    backgroundImage: 'none',
                    backdropFilter: 'none',
                    '&:hover': {
                      transform: 'none',
                      boxShadow: 'none',
                      borderColor:
                        themeMode === 'dark' ? 'rgba(45,212,191,0.3)' : 'rgba(13,148,136,.3)',
                    },
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      リストを編集
                    </Typography>
                    <TextField
                      fullWidth
                      label="リスト名"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="説明"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="ドメイン（1行に1つ、またはカンマ区切り）"
                      value={editDomains}
                      onChange={(e) => setEditDomains(e.target.value)}
                      multiline
                      rows={4}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        onClick={handleSaveEdit}
                        disabled={saving || !editName.trim()}
                        startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                      >
                        {saving ? '保存中...' : '保存'}
                      </Button>
                      <Button variant="outlined" onClick={() => setEditingList(null)}>
                        キャンセル
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                // 表示モード
                <Accordion key={list.id} disableGutters elevation={0} sx={accordionSx}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {list.name}
                      </Typography>
                      <Chip label={`${list.domains.length}件`} size="small" />
                      <Box sx={{ flexGrow: 1 }} />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartEdit(list);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(list.id);
                        }}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      {list.description}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {list.domains.map((domain, index) => (
                        <Chip key={index} label={domain} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ),
            )
          )}
        </>
      )}
    </AppDialog>
  );
};

export default DomainListManager;
